/**
 * MapLibre style-object factory for the Meridian Map view.
 *
 * Ships both the light and dark variants (REQ-UI-040 + REQ-UI-045).
 * The companion :func:`applyMapTheme` (``use-map-theme.ts``)
 * performs the *runtime* flip without a full ``setStyle`` so the
 * imperatively-added data layers (snapshot / cluster / trail /
 * track-tail / event) survive a theme change; this factory is used
 * only to build the **initial** style at construction time so the
 * very first paint is correct in whichever theme the operator's
 * preference resolves to.
 *
 * # Dark basemap: real dark tiles vs. dev darkening filter
 *
 * Two ways a dark basemap is produced, selected by whether a dark
 * tile layer name is configured (``darkLayer``):
 *
 * 1. **Production** — the deployment-environment tile server serves
 *    a dark-compatible raster layer. The active theme drives a
 *    request for that ``{layer}`` from the BFF tile-proxy
 *    (ADR-016 + REQ-UI-045). When ``darkLayer`` is supplied, the
 *    dark style points the ``meridian-base`` source at it.
 * 2. **Dev / harness** — the harness tile source is NRL BlueMarble
 *    *daytime* imagery; there is no dark layer to request. When
 *    ``darkLayer`` is **absent** (the dev default), the dark style
 *    keeps the light tiles and bakes a MapLibre raster *darkening*
 *    paint (:const:`DARK_RASTER_FILTER`) into the raster layer so
 *    the canvas reads as dark on first paint. :func:`applyMapTheme`
 *    applies/clears the same filter on a live theme flip.
 *
 * # Why inline rather than BFF-served style.json
 *
 * The plan flagged this open decision. For PR 2 (M1) the choice
 * is an **inline minimal style** for three reasons:
 *
 * 1. Air-gap posture (CLAUDE.md §8.2): a BFF-served style.json
 *    would require a second endpoint surface; for MVP the style
 *    is a static, code-derived constant and ships in the bundle.
 * 2. One-file change: the style is small (one raster source +
 *    one raster layer) and reads more clearly inline than as a
 *    fetched artifact.
 * 3. Clean seam for future BFF-served path: callers can swap the
 *    factory for ``fetch('/api/map-style')`` without changing the
 *    Map component's public API. A theme-aware BFF style endpoint
 *    is the natural extension if Stream B determines that
 *    deployment-environment style identifiers (REQ-UI-045) belong
 *    on the BFF rather than baked into the bundle.
 *
 * # Tile source contract (config-driven, ADR-016 amended 2026-06-10)
 *
 * Tile URLs are composed from a :type:`TileSourceDescriptor`
 * advertised by ``GET /api/config`` (``map.tile_options[]``), not a
 * hard-coded proxy path. The descriptor carries a MapLibre
 * ``url_template`` that already contains the ``{z}/{x}/{y}``
 * placeholders (which MapLibre expands per-tile) plus ``{layer}`` /
 * ``{format}`` placeholders this factory substitutes from the
 * descriptor's ``layer`` / ``format`` fields:
 *
 * 1. **Browser-direct** — an *absolute* ``url_template`` (e.g. NRL
 *    Blue Marble ``https://geoint.nrlssc.navy.mil/.../{z}/{y}/{x}.
 *    {format}``). The operator's browser reaches a per-network tile
 *    server the air-gapped backend cannot (REQ-UI-033 / REQ-UI-034);
 *    the CSP that authorises the absolute host is the BFF/nginx
 *    increment (REQ-UI-035 — a *later* increment, not this one).
 * 2. **BFF proxy (retained fallback)** — a *relative*
 *    ``/api/tiles/{layer}/{z}/{x}/{y}.{format}`` ``url_template``
 *    routes same-origin through the BFF tile-proxy landed in commit
 *    ``2fbad49`` (REQ-UI-034). MapLibre's default ``fetch`` uses the
 *    document origin, so the relative form rides the Vite dev proxy /
 *    deployed nginx → BFF.
 *
 * Both forms flow through the **same** ``{layer}`` / ``{format}``
 * substitution (:func:`composeTileUrlFromTemplate`), so absolute vs.
 * relative is purely a property of the advertised descriptor — no
 * branch in this factory.
 *
 * The legacy positional ``tileBaseUrl`` form (``"/api/tiles"`` + the
 * factory appending ``/{layer}/{z}/{x}/{y}.{format}``) is retained
 * for callers/tests that predate the descriptor; it synthesises the
 * relative proxy template and runs it through the identical
 * substitution. New production wiring passes a descriptor-derived
 * :type:`TileSourceSpec` via the options object.
 *
 * The harness's default descriptor layer is ``BlueMarble_AUTO``
 * matching NRL's primary base (per
 * ``docs/engineering-docs/misc/T-05-deferral-map-tiles-planning-
 * notes.md`` §"Harness-side selection"). Production deployments
 * advertise their own descriptors; the layer / format / max-zoom /
 * url-template all ride the descriptor, not a constant.
 *
 * # Format
 *
 * ``png`` for raster tiles. NRL's BlueMarble layer is raster
 * imagery. ``pbf`` for vector tiles (OpenMapTiles schema) — rendered by
 * :file:`style-vector.ts` with client-side English labels as of
 * 2026-06-12; the descriptor's ``type`` selects the raster/vector path.
 *
 * Cross-references:
 *   - REQ-UI-002 (MapLibre GL JS).
 *   - REQ-UI-033 / REQ-UI-034 (browser-direct tiles + retained BFF
 *     proxy fallback; ADR-016 amended 2026-06-10).
 *   - REQ-UI-035 (BFF-only network calls — the CSP that authorises an
 *     absolute browser-direct host is the BFF/nginx increment, NOT
 *     this one; relative descriptors stay same-origin BFF).
 *   - REQ-UI-040 (paired themes).
 *   - REQ-UI-045 (theme-aware tile style — ``dark_layer`` from the
 *     descriptor).
 *   - REQ-UI-091 (Profile-selected basemap — a later consumer; this
 *     factory takes whichever descriptor the caller resolved).
 *   - ADR-016 §4 + 2026-05-12 + 2026-06-10 amendments.
 *   - docs/engineering/detail/storyboards/shared/design-system.md.
 */
import type { StyleSpecification } from "maplibre-gl";
import type { ResolvedTheme } from "@/theme/tokens";
import { THEME_TOKENS } from "@/theme/tokens";
import {
  DEFAULT_GLYPHS_BASE_URL,
  glyphsUrlFromBase,
  MAP_BACKGROUND_LAYER_ID,
  MAP_BASE_RASTER_LAYER_ID,
  MAP_BASE_SOURCE_ID,
} from "@/map/map-constants";
import { buildVectorBasemap, VECTOR_BASEMAP_SOURCE_ID } from "@/map/style-vector";

// Re-export the shared basemap constants so existing callers that import
// them from ``@/map/style`` are unaffected (they now live in the leaf
// ``map-constants.ts`` so :file:`style-vector.ts` can import them without
// a circular dependency on this module — the cold-start acyclic-graph
// discipline).
export {
  DEFAULT_GLYPHS_BASE_URL,
  glyphsUrlFromBase,
  MAP_BACKGROUND_LAYER_ID,
  MAP_BASE_RASTER_LAYER_ID,
  MAP_BASE_SOURCE_ID,
};

/**
 * Meridian map theme — distinct from the application theme to
 * preserve the seam that REQ-UI-045 requires (the active app
 * theme drives the tile-style request).
 *
 * - ``light`` — the storyboard default; ships in this PR.
 * - ``dark`` — the dark basemap variant (real dark tiles when a
 *   ``darkLayer`` is configured; light tiles + a raster darkening
 *   filter in dev otherwise).
 * - ``system`` — resolved to ``light`` / ``dark`` from
 *   ``prefers-color-scheme`` *before* this factory is called
 *   (the caller passes the resolved value per REQ-UI-041). Passing
 *   ``"system"`` here is treated as ``"light"`` defensively; the
 *   Map component resolves it via :func:`useResolvedTheme` so the
 *   literal never actually reaches this factory.
 *
 * Cross-references:
 *   - REQ-UI-040 / REQ-UI-041 / REQ-UI-045.
 */
export type MapTheme = "light" | "dark" | "system";

/**
 * The harness's default tile layer name.
 *
 * Production deployments override per the deployment-environment
 * tile-server configuration (REQ-UI-033 / REQ-UI-034). Storyboard
 * frames are layer-agnostic; the operator-facing behaviour is the
 * same regardless of which named raster layer is served.
 *
 * Cross-references:
 *   - docs/engineering-docs/misc/T-05-deferral-map-tiles-planning-
 *     notes.md §"Harness-side selection".
 *   - ADR-016 amended 2026-05-12.
 */
export const DEFAULT_TILE_LAYER = "BlueMarble_AUTO";

/**
 * The format Meridian's harness requests from NRL — raster PNG.
 *
 * The BFF tile-proxy validates ``{format}`` against
 * ``{png, jpeg, jpg, webp, pbf}`` per ``packages/meridian_bff/
 * src/meridian_bff/_tiles.py``. PNG is the default raster format
 * for NRL's BlueMarble layer.
 *
 * Cross-references:
 *   - ADR-016 amended 2026-05-12.
 */
export const DEFAULT_TILE_FORMAT = "png";

/**
 * Default source ``maxzoom`` (the overzoom guard) when a caller uses
 * the legacy ``tileBaseUrl`` form and supplies no explicit value.
 *
 * Config-driven callers pass the descriptor's ``max_zoom`` instead —
 * NRL Blue Marble caps at 8, CADRG/org rasters differ. The source
 * ``maxzoom`` is the level past which MapLibre **overzooms** (scales
 * the last served tile) rather than requesting a tile the server
 * doesn't have; this replaces the old BFF proxy 502-on-too-deep body
 * sniff for the browser-direct path (REQ-UI-033 / REQ-UI-034).
 *
 * 18 preserves the pre-descriptor behaviour for the legacy form so
 * existing callers/tests are unaffected.
 *
 * Cross-references:
 *   - REQ-UI-033 / REQ-UI-034; ADR-016 amended 2026-06-10.
 */
export const DEFAULT_TILE_MAX_ZOOM = 18;

/**
 * Default BFF tile-proxy base path — the relative, same-origin
 * fallback (REQ-UI-034). Used to synthesise the legacy
 * ``/api/tiles/{layer}/{z}/{x}/{y}.{format}`` ``url_template`` when a
 * caller passes the positional ``tileBaseUrl`` form instead of a
 * descriptor-derived :type:`TileSourceSpec`.
 *
 * Cross-references:
 *   - REQ-UI-034 (retained BFF proxy); REQ-UI-035 (same-origin).
 */
export const DEFAULT_TILE_PROXY_BASE_URL = "/api/tiles";

/**
 * The tile-source parameters this factory needs, distilled from a
 * :type:`TileSourceDescriptor` (``chrome/use-deployment-config.ts``).
 *
 * Decoupled from the wire descriptor so :file:`style.ts` does not
 * import the chrome/config layer (keeps the map module's dependency
 * graph acyclic) and so tests can build a spec inline. The resolving
 * hook (:func:`useTileDescriptor`) maps the descriptor → this shape.
 *
 * - ``urlTemplate`` — the MapLibre tile ``url_template``. **Absolute**
 *   ⇒ browser-direct (REQ-UI-033); **relative** (``/api/tiles/...``)
 *   ⇒ same-origin BFF proxy (REQ-UI-034). Carries ``{z}/{x}/{y}``
 *   (expanded by MapLibre) plus ``{layer}`` / ``{format}`` (this
 *   factory substitutes them from ``layer`` / ``format``).
 * - ``layer`` / ``format`` — substituted into the template.
 * - ``maxZoom`` — the source ``maxzoom`` overzoom guard.
 * - ``darkUrlTemplate`` — the dark-theme template (REQ-UI-045). When
 *   ``null`` / absent, the dark theme keeps ``urlTemplate`` and bakes
 *   the in-shader :const:`DARK_RASTER_FILTER` darkening instead.
 * - ``darkLayer`` — the named layer substituted into ``{layer}`` of
 *   ``darkUrlTemplate`` (the descriptor's ``dark_layer``). Defaults to
 *   ``layer`` when absent.
 * - ``attribution`` — the on-render attribution string (legally
 *   required for OSM/ODbL-derived sources; NRL/NASA credited too),
 *   surfaced through MapLibre's (compact, collapsed) Attribution
 *   control via the source's ``attribution`` field.
 *
 * Cross-references:
 *   - REQ-UI-033 / REQ-UI-034 / REQ-UI-045 / REQ-UI-091.
 *   - ADR-016 amended 2026-06-10.
 */
export interface TileSourceSpec {
  /**
   * The basemap kind (descriptor ``type``; ADR-016 amended 2026-06-10).
   *
   * - ``"raster"`` — pre-rendered image tiles; ``urlTemplate`` is a
   *   ``{z}/{x}/{y}`` tile URL composed into a MapLibre raster source.
   *   The dark theme uses a real dark layer (``darkUrlTemplate``) or
   *   the in-shader :const:`DARK_RASTER_FILTER` darkening.
   * - ``"vector"`` — OpenMapTiles vector tiles; ``urlTemplate`` is a
   *   **TileJSON URL** consumed by a MapLibre vector source, styled by
   *   :func:`buildVectorBasemap` with paired light/dark palettes
   *   in-style (no in-shader darkening — ``darkUrlTemplate`` /
   *   ``darkLayer`` are ignored for vector; the dark variant is a
   *   different layer paint, applied per-layer on a theme flip).
   *
   * Optional + defaulting to ``"raster"`` so legacy callers / tests that
   * predate the vector path keep their raster behaviour unchanged.
   */
  readonly type?: "raster" | "vector";
  /**
   * MapLibre tile ``url_template`` (absolute ⇒ browser-direct). For a
   * ``"vector"`` spec this is the **TileJSON URL** (MapLibre reads
   * tiles[]/zoom/vector_layers from it), not a ``{z}/{x}/{y}`` template.
   */
  readonly urlTemplate: string;
  /** Named layer substituted into ``{layer}``. */
  readonly layer: string;
  /** Tile format substituted into ``{format}``. */
  readonly format: string;
  /** Source ``maxzoom`` overzoom guard (descriptor ``max_zoom``). */
  readonly maxZoom: number;
  /**
   * Dark-theme ``url_template`` (REQ-UI-045). ``null`` ⇒ keep the
   * light template + in-shader darkening (dev / NRL Blue Marble).
   */
  readonly darkUrlTemplate?: string | null;
  /**
   * Named layer for ``{layer}`` of ``darkUrlTemplate`` (the
   * descriptor's ``dark_layer``). Defaults to ``layer`` when absent.
   */
  readonly darkLayer?: string | null;
  /** On-render attribution string (source ``attribution`` field). */
  readonly attribution: string;
}

/**
 * Substitute ``{layer}`` / ``{format}`` into a MapLibre tile
 * ``url_template``, leaving ``{z}`` / ``{x}`` / ``{y}`` intact for
 * MapLibre to expand per-tile.
 *
 * The single substitution primitive both the browser-direct
 * (absolute) and BFF-proxy (relative) paths flow through, so the two
 * differ only by the descriptor's ``url_template`` value — never by a
 * code branch. ``{layer}`` / ``{format}`` are replaced globally (a
 * template may reference ``{format}`` more than once); unknown
 * placeholders are left untouched.
 *
 * @param urlTemplate - The descriptor's tile ``url_template``.
 * @param layer - The named layer for ``{layer}``.
 * @param format - The tile format for ``{format}``.
 * @returns The MapLibre-ready tile URL (still carrying ``{z}/{x}/{y}``).
 *
 * Cross-references:
 *   - REQ-UI-033 / REQ-UI-034; ADR-016 amended 2026-06-10.
 */
export function composeTileUrlFromTemplate(
  urlTemplate: string,
  layer: string,
  format: string,
): string {
  return urlTemplate.replaceAll("{layer}", layer).replaceAll("{format}", format);
}

/**
 * Synthesise the legacy relative proxy ``url_template`` from a
 * ``tileBaseUrl`` (e.g. ``"/api/tiles"`` →
 * ``/api/tiles/{layer}/{z}/{x}/{y}.{format}``).
 *
 * Retained so callers/tests predating the descriptor keep working:
 * :func:`buildMapStyle` and :func:`applyMapTheme` route the result
 * through the same :func:`composeTileUrlFromTemplate` substitution as
 * a real (possibly absolute) descriptor template. Normalises a
 * trailing slash on the base.
 *
 * Cross-references:
 *   - REQ-UI-034 (retained BFF proxy form).
 */
export function legacyProxyUrlTemplate(tileBaseUrl: string): string {
  const normalizedBase = tileBaseUrl.endsWith("/") ? tileBaseUrl.slice(0, -1) : tileBaseUrl;
  return `${normalizedBase}/{layer}/{z}/{x}/{y}.{format}`;
}

/**
 * Map background fallback color, light theme.
 *
 * Anchored on ``--map-bg-from`` from
 * ``docs/engineering/detail/storyboards/shared/design-system.md``
 * §"Map background (theme-dependent)" — the value the storyboard
 * pre-tile background uses while tiles load. Hard-coded here
 * rather than read from CSS custom properties because MapLibre's
 * style-spec wants a literal color value at style-construction
 * time, not a CSS-variable expression. Stream B parameterises by
 * theme.
 *
 * Cross-references:
 *   - REQ-UI-040.
 */
const LIGHT_MAP_BG = THEME_TOKENS.light.map.bgFrom;

/**
 * Map background fallback color, dark theme.
 *
 * Sourced from :data:`THEME_TOKENS.dark.map.bgFrom` — the canonical
 * TypeScript mirror of ``design-system.md`` §"Map background
 * (theme-dependent)" ``--map-bg-from``. ``tokens.ts`` carries the
 * 2026-05-26 Appaloosa-refresh value (``#181c27``); the
 * design-system markdown table still lists the pre-refresh
 * ``#1e293b`` (stale — flagged for a docs follow-up). Reading from
 * :data:`THEME_TOKENS` keeps the map canvas background exactly
 * aligned with the rest of the dark UI chrome (the value Tailwind
 * paints everywhere else), which is the property that matters for
 * REQ-UI-046 visual cohesion.
 *
 * Hard-baked as a literal hex at style-construction time for the
 * same reason as :const:`LIGHT_MAP_BG` — MapLibre's style-spec
 * wants a literal color, not a CSS-variable expression.
 *
 * Cross-references:
 *   - REQ-UI-040 / REQ-UI-046.
 *   - ``docs/engineering/detail/storyboards/shared/design-system.md``
 *     §"Map background (theme-dependent)".
 */
const DARK_MAP_BG = THEME_TOKENS.dark.map.bgFrom;

// ``MAP_BASE_SOURCE_ID`` / ``MAP_BACKGROUND_LAYER_ID`` /
// ``MAP_BASE_RASTER_LAYER_ID`` now live in :file:`map-constants.ts` (the
// shared leaf) and are imported + re-exported at the top of this file —
// see the import block — so :file:`style-vector.ts` can use the
// background-layer id without a circular import on this module.

/**
 * Raster paint properties that darken the (daytime) dev basemap so
 * it reads as a dark-mode canvas.
 *
 * Used in two places that MUST stay in lock-step:
 *   - This factory bakes these into the raster layer's ``paint``
 *     when building the **dark** style with no ``darkLayer`` (dev
 *     first paint).
 *   - :func:`applyMapTheme` calls ``setPaintProperty`` with these
 *     values on a live light→dark flip (dev path).
 *
 * The values are a pragmatic darkening — drop the brightness
 * ceiling, desaturate, lift contrast slightly — tuned during the
 * ``verified-ui-done`` pass so the BlueMarble imagery reads as a
 * muted dark basemap that keeps the operator-tooling "data is the
 * figure, chrome is the ground" register (agent rule 10) while
 * status markers retain WCAG-AA contrast (REQ-UI-046). One-line
 * tunables; not a configuration knob (no production need — prod
 * uses a real dark tile layer).
 *
 * Cross-references:
 *   - REQ-UI-040 / REQ-UI-046.
 *   - ADR-016 §4.
 */
export const DARK_RASTER_FILTER: Readonly<{
  "raster-brightness-max": number;
  "raster-saturation": number;
  "raster-contrast": number;
}> = {
  "raster-brightness-max": 0.5,
  "raster-saturation": -0.3,
  "raster-contrast": 0.1,
};

/**
 * MapLibre raster paint defaults — the values that reset the dev
 * darkening filter back to "no filter" for the light theme.
 *
 * These are the style-spec defaults (``raster-brightness-max`` 1,
 * ``raster-saturation`` 0, ``raster-contrast`` 0); naming them here
 * keeps :func:`applyMapTheme`'s light path explicit rather than
 * relying on a reader to recall the spec defaults.
 *
 * Cross-references:
 *   - REQ-UI-040.
 */
export const LIGHT_RASTER_FILTER: Readonly<{
  "raster-brightness-max": number;
  "raster-saturation": number;
  "raster-contrast": number;
}> = {
  "raster-brightness-max": 1,
  "raster-saturation": 0,
  "raster-contrast": 0,
};

/**
 * Build a MapLibre style-object for the Meridian Map view.
 *
 * Two calling forms, sharing one tile-URL substitution primitive:
 *
 *   - **Descriptor-driven (production; ADR-016 amended 2026-06-10)** —
 *     pass a :type:`TileSourceSpec` as ``spec`` (resolved from the
 *     ``GET /api/config`` ``map.tile_options[]`` descriptor). Its
 *     ``urlTemplate`` may be **absolute** (browser-direct,
 *     REQ-UI-033) or **relative** (``/api/tiles/...`` → BFF proxy,
 *     REQ-UI-034); ``maxZoom`` sets the source overzoom guard;
 *     ``darkUrlTemplate`` / ``attribution`` come from the descriptor.
 *     When ``spec`` is supplied, the positional ``layer`` / ``format``
 *     / ``darkLayer`` arguments are **ignored** (the spec is
 *     authoritative).
 *   - **Legacy (pre-descriptor; retained for callers/tests)** — pass
 *     a ``tileBaseUrl`` (e.g. ``"/api/tiles"``); the factory
 *     synthesises the relative proxy template and threads it through
 *     the identical substitution.
 *
 * @param theme - The active *resolved* map theme (``"light"`` or
 *     ``"dark"``; ``"system"`` is treated as ``"light"`` — the
 *     caller resolves it first per REQ-UI-041). The dark variant
 *     points the basemap source at the dark template/layer when one
 *     is configured (production), otherwise keeps the light tiles and
 *     bakes the :const:`DARK_RASTER_FILTER` darkening (dev / harness).
 * @param tileBaseUrl - Legacy BFF tile-proxy base URL (e.g.
 *     ``"/api/tiles"``). Ignored when ``spec`` is supplied. Composed
 *     against the document origin in production via Vite's
 *     ``/api/*`` proxy; the factory appends ``/{layer}/{z}/{x}/{y}.
 *     {format}``.
 * @param layer - Legacy named layer (ignored when ``spec`` is
 *     supplied). Defaults to :const:`DEFAULT_TILE_LAYER`.
 * @param format - Legacy tile format (ignored when ``spec`` is
 *     supplied). Defaults to :const:`DEFAULT_TILE_FORMAT`.
 * @param glyphsBaseUrl - Base path for the BFF glyphs endpoint.
 *     Defaults to :const:`DEFAULT_GLYPHS_BASE_URL`.
 * @param darkLayer - Legacy named dark layer (ignored when ``spec``
 *     is supplied). When set + ``theme`` is dark, the basemap
 *     requests it; when omitted, dark bakes :const:`DARK_RASTER_FILTER`.
 * @param spec - Descriptor-derived :type:`TileSourceSpec`. When
 *     present it overrides all of the legacy tile params above; this
 *     is the config-driven production path.
 * @returns A :class:`maplibre-gl.StyleSpecification` ready to pass
 *     to ``new Map({ style })``.
 *
 * Cross-references:
 *   - REQ-UI-002 (MapLibre GL JS rendering).
 *   - REQ-UI-033 / REQ-UI-034 (browser-direct + retained proxy).
 *   - REQ-UI-035 (BFF-only — CSP for an absolute host is the
 *     BFF/nginx increment, not this one).
 *   - REQ-UI-040 / REQ-UI-045 (paired themes + theme-aware tiles).
 *   - REQ-UI-046 (status-colour legibility on the dark basemap).
 *   - REQ-UI-091 (Profile-selected basemap — later consumer).
 *   - ADR-016 §4 + 2026-05-12 + 2026-06-10 amendments.
 */
export function buildMapStyle(
  theme: MapTheme,
  tileBaseUrl: string,
  layer: string = DEFAULT_TILE_LAYER,
  format: string = DEFAULT_TILE_FORMAT,
  glyphsBaseUrl: string = DEFAULT_GLYPHS_BASE_URL,
  darkLayer?: string,
  spec?: TileSourceSpec | null,
): StyleSpecification {
  // ``"system"`` must be resolved by the caller (REQ-UI-041); treat
  // anything other than ``"dark"`` as light defensively so a stray
  // literal can't blank the canvas. The Map component passes a
  // resolved value, so this is belt-and-suspenders, not the live
  // path.
  const isDark = theme === "dark";

  // Resolve the effective tile params: the descriptor-derived ``spec``
  // is authoritative when present; otherwise synthesise the legacy
  // relative proxy template from ``tileBaseUrl`` + the positional
  // ``layer`` / ``format`` / ``darkLayer``. Both paths converge on a
  // single ``url_template`` (+ optional dark template) that carries
  // ``{layer}`` / ``{format}`` placeholders for substitution below.
  const lightUrlTemplate = spec?.urlTemplate ?? legacyProxyUrlTemplate(tileBaseUrl);
  // Dark-theme template: a spec carries its own ``darkUrlTemplate``;
  // the legacy form reuses the same (proxy) template but swaps the
  // layer name (``darkLayer``), so the dark template equals the light
  // one whenever a legacy ``darkLayer`` is configured, else null.
  const darkUrlTemplate: string | null = spec
    ? (spec.darkUrlTemplate ?? null)
    : darkLayer != null
      ? lightUrlTemplate
      : null;
  const effectiveLayer = spec?.layer ?? layer;
  const effectiveFormat = spec?.format ?? format;
  const effectiveMaxZoom = spec?.maxZoom ?? DEFAULT_TILE_MAX_ZOOM;
  const effectiveAttribution =
    spec?.attribution ??
    "Tiles via Meridian BFF (harness: NRL Tile Server, US Navy / NASA Blue Marble)";
  // The named layer to substitute into the dark template's ``{layer}``:
  // the spec's ``darkLayer`` (the descriptor's ``dark_layer``) when set,
  // the legacy ``darkLayer`` otherwise, falling back to the light layer.
  const effectiveDarkLayer = spec
    ? (spec.darkLayer ?? effectiveLayer)
    : (darkLayer ?? effectiveLayer);

  // Does a real dark basemap exist (vs. in-shader darkening)? For a
  // spec it's whether ``darkUrlTemplate`` is non-null; for the legacy
  // form it's whether a ``darkLayer`` was passed.
  const hasDarkBasemap = spec ? spec.darkUrlTemplate != null : darkLayer != null;

  // The tile URL the active theme actually requests. Dark with a real
  // dark basemap → the dark template + dark layer; otherwise the light
  // template + light layer (dark is then darkened in-shader).
  const tileUrlTemplate =
    isDark && hasDarkBasemap && darkUrlTemplate
      ? composeTileUrlFromTemplate(darkUrlTemplate, effectiveDarkLayer, effectiveFormat)
      : composeTileUrlFromTemplate(lightUrlTemplate, effectiveLayer, effectiveFormat);

  // Glyphs URL template — MapLibre substitutes ``{fontstack}`` +
  // ``{range}`` per symbol layer. Same-origin BFF endpoint per
  // REQ-UI-035 (issue #205); the BFF serves the pre-generated SDF
  // atlas under ``/api/glyphs/<fontstack>/<range>.pbf``.
  const glyphsUrlTemplate = glyphsUrlFromBase(glyphsBaseUrl);

  // ── No basemap yet (#1002) ───────────────────────────────────────
  // An explicit ``null`` spec means "the descriptor is unresolved" (the
  // ``/api/config`` query is pending or errored): build a style with NO
  // basemap source — just the theme background — so the canvas paints
  // and MapLibre fires ``load`` immediately, and ``rebuildBasemap`` adds
  // the basemap when the descriptor lands. Nothing external is fetched.
  // (``undefined`` keeps its historical meaning — the positional legacy
  // raster via the BFF proxy — so only an explicit ``null`` takes this
  // branch.) Glyphs stay wired so overlay symbol layers can label.
  if (spec === null) {
    return {
      version: 8,
      name: isDark ? "meridian-nobasemap-dark" : "meridian-nobasemap-light",
      sources: {},
      layers: [
        {
          id: MAP_BACKGROUND_LAYER_ID,
          type: "background",
          paint: {
            "background-color": isDark ? DARK_MAP_BG : LIGHT_MAP_BG,
          },
        },
      ],
      glyphs: glyphsUrlTemplate,
    };
  }

  // ── Vector branch (ADR-016 amended 2026-06-10) ───────────────────
  // When the descriptor advertises ``type: "vector"``, build the
  // OpenMapTiles street-map style instead of the single raster
  // source/layer. The vector source consumes the descriptor's
  // ``urlTemplate`` as a **TileJSON URL** (MapLibre reads
  // tiles[]/zoom/vector_layers from it — no ``{z}/{x}/{y}`` composition).
  // Paired light/dark palettes are carried IN-STYLE (no in-shader
  // darkening filter — that's a raster-only construct), so the
  // theme-resolved ``buildVectorBasemap`` produces the correct first
  // paint; the runtime flip re-paints per-layer (``applyMapTheme``).
  //
  // The overlay data layers (snapshot / cluster / detection / alert /
  // geometry) are still added imperatively on ``'load'`` and append
  // ABOVE these basemap layers, so they compose on top exactly as they
  // do over the raster basemap. The descriptor ``attribution`` is
  // threaded onto the vector source (ODbL for OSM-derived sources).
  if (spec?.type === "vector") {
    const resolvedTheme: ResolvedTheme = isDark ? "dark" : "light";
    const { sources, layers } = buildVectorBasemap(resolvedTheme, lightUrlTemplate, glyphsBaseUrl);
    // Override the vector source's default attribution with the
    // descriptor's (deployment-authoritative; legally required string).
    const vectorSource = sources[VECTOR_BASEMAP_SOURCE_ID];
    return {
      version: 8,
      name: isDark ? "meridian-vector-dark" : "meridian-vector-light",
      sources: {
        [VECTOR_BASEMAP_SOURCE_ID]: { ...vectorSource, attribution: effectiveAttribution },
      },
      layers,
      glyphs: glyphsUrlTemplate,
    };
  }

  // The raster layer's darkening paint. Applied only in the dev /
  // harness dark path (dark theme, no real dark basemap): the
  // BlueMarble daytime imagery is darkened in-shader. With a real dark
  // basemap the tiles are already dark, so we leave the raster paint at
  // the spec defaults. Light always uses defaults.
  const useDevDarkFilter = isDark && !hasDarkBasemap;
  const rasterPaint = useDevDarkFilter ? { ...DARK_RASTER_FILTER } : undefined;

  return {
    version: 8,
    name: isDark ? "meridian-dark" : "meridian-light",
    // MapLibre's raster source supports the ``{z}/{x}/{y}``
    // placeholder pattern in ``tiles[]`` URLs. The layer + format are
    // already substituted in; ``{z}/{x}/{y}`` remain for MapLibre.
    sources: {
      [MAP_BASE_SOURCE_ID]: {
        type: "raster",
        tiles: [tileUrlTemplate],
        // 256 px tiles — the canonical web-Mercator tile size NRL
        // (and most WMTS servers) emit. MapLibre supports 512 too;
        // we follow upstream defaults rather than introduce a
        // configuration knob in M1.
        tileSize: 256,
        // Source ``maxzoom`` overzoom guard (REQ-UI-033 / REQ-UI-034;
        // descriptor ``max_zoom`` — NRL Blue Marble caps at 8). Past
        // this level MapLibre scales the last served tile rather than
        // requesting one the server lacks — this is what replaces the
        // old BFF proxy 502-on-too-deep body sniff for the
        // browser-direct path.
        minzoom: 0,
        maxzoom: effectiveMaxZoom,
        // On-render attribution (descriptor ``attribution``). Surfaced
        // by the compact, click-to-expand AttributionControl
        // (``use-map.ts``) which reads this source field. Legally
        // required for OSM/ODbL-derived sources; NRL/NASA credited too.
        attribution: effectiveAttribution,
      },
    },
    layers: [
      // Background layer — shows while tiles are still loading and
      // when the operator pans past the tile coverage. Anchored on
      // ``--map-bg-from`` from design-system.md; theme-dependent so
      // the pre-tile flash matches the active theme (REQ-UI-040).
      {
        id: MAP_BACKGROUND_LAYER_ID,
        type: "background",
        paint: {
          "background-color": isDark ? DARK_MAP_BG : LIGHT_MAP_BG,
        },
      },
      {
        id: MAP_BASE_RASTER_LAYER_ID,
        type: "raster",
        source: MAP_BASE_SOURCE_ID,
        minzoom: 0,
        maxzoom: 22,
        // Dev dark path only — darken the daytime imagery in-shader.
        // ``undefined`` (light, or production dark tiles) leaves the
        // raster paint at MapLibre's spec defaults.
        ...(rasterPaint ? { paint: rasterPaint } : {}),
      },
    ],
    // Glyphs URL for the cluster-count symbol layer (issue #205).
    // The BFF serves the SDF atlas same-origin per REQ-UI-035;
    // ``cluster-layer-impl`` requests the "DM Sans Medium"
    // fontstack. The sprite is still omitted — no icon layers yet.
    glyphs: glyphsUrlTemplate,
  };
}

/**
 * Descriptor-first convenience entry to :func:`buildMapStyle` — the
 * production path (ADR-016 amended 2026-06-10).
 *
 * Builds the style from a resolved :type:`TileSourceSpec` without the
 * positional legacy tile arguments (which the spec overrides anyway),
 * so the Map component's once-at-mount style build reads cleanly. The
 * legacy ``tileBaseUrl`` slot is filled with
 * :const:`DEFAULT_TILE_PROXY_BASE_URL` purely to satisfy the
 * underlying signature; it is ignored because ``spec`` is supplied.
 *
 * @param theme - The resolved map theme (``"light"`` / ``"dark"``).
 * @param spec - The descriptor-derived tile source, or ``null`` while the
 *     descriptor is unresolved — a background-only style with no basemap
 *     source (#1002; see :func:`buildMapStyle`).
 * @param glyphsBaseUrl - Base path for the BFF glyphs endpoint.
 *     Defaults to :const:`DEFAULT_GLYPHS_BASE_URL`.
 * @returns A :class:`maplibre-gl.StyleSpecification`.
 *
 * Cross-references:
 *   - REQ-UI-033 / REQ-UI-034 / REQ-UI-045; ADR-016 amended 2026-06-10.
 *   - REQ-UI-035 / #1002 (no network guess while unresolved).
 */
export function buildMapStyleFromSpec(
  theme: MapTheme,
  spec: TileSourceSpec | null,
  glyphsBaseUrl: string = DEFAULT_GLYPHS_BASE_URL,
): StyleSpecification {
  return buildMapStyle(
    theme,
    DEFAULT_TILE_PROXY_BASE_URL,
    undefined,
    undefined,
    glyphsBaseUrl,
    undefined,
    spec,
  );
}
